import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, TCoreButtonViewProps, TCoreTextStyleProps } from '@core/widgets';
import { TCommonData, TViewCommonOption, TWidget } from '@libs/types';
import React, { ElementType, MouseEventHandler } from 'react';
import { TRowActionData } from '../../types/data';
import { TRowActionOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import styles from './styles/rowAction.module.css';

export type TRowActionViewProps = {
    uid: TWidget<TCommonData>['uid'];
    widget?: TWidget<TRowActionData, TRowActionOption>;
    title?: string;
    subtitle?: string;
    helperText?: string;
    iconName?: string;
    isSubtitleTextPositionTop?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    as?: ElementType;
    children?: React.ReactNode;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames' | 'styleName'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--color-white)',
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    gap: 16,
    fillContainer: true,
    cursorDisabled: 'default',
    textAlign: 'start',
    disabled: {
        main: 'var(--color-white)',
    },
    hovered: {
        main: 'var(--color-grey_16_lc)',
    },
    pressed: {
        main: 'var(--color-grey_8_lc)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    maxLines: 2,
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    maxLines: 2,
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

/**
 * Composable building block for the RowAction widget.
 * Owns only the rendering logic; accepts pre-resolved behavior props from the parent.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized,
 * which is sufficient for standalone usages.
 */
export const RowActionView = ({
    uid,
    widget,
    title,
    subtitle,
    helperText,
    iconName,
    isSubtitleTextPositionTop = true,
    isDisabled,
    isHide,
    onClick,
    as = 'button',
    children,
    parentStyleNames = [],
}: TRowActionViewProps) => {
    return (
        <CoreButtonView
            widget={widget as TCoreButtonViewProps['widget']}
            text={title ?? subtitle ?? helperText}
            isDisabled={isDisabled}
            isHide={isHide}
            onClick={onClick}
            as={as}
            parentStyleNames={parentStyleNames}
            styleName="rowAction"
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin: widget?.options?.margin,
            }}
        >
            <div className={styles.textContent}>
                <div className={styles.topTextContent}>
                    {isSubtitleTextPositionTop && subtitle && (
                        <CoreTextView
                            uid={'subtitle-' + uid}
                            text={subtitle}
                            styleProps={SUBTITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionSubtitle"
                        />
                    )}
                    {title && (
                        <CoreTextView
                            uid={'title-' + uid}
                            text={title}
                            styleProps={TITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionTitle"
                        />
                    )}
                    {!isSubtitleTextPositionTop && subtitle && (
                        <CoreTextView
                            uid={'subtitle-' + uid}
                            text={subtitle}
                            styleProps={SUBTITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionSubtitle"
                        />
                    )}
                </div>
                {isSubtitleTextPositionTop && helperText && (
                    <CoreTextView
                        uid={'helperText-' + uid}
                        text={helperText}
                        styleProps={SUBTITLE_STYLE_PROPS}
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowActionHelperText"
                    />
                )}
            </div>
            {iconName && (
                <VisualView
                    uid={`icon-${uid}`}
                    iconName={iconName}
                    size={'small'}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowActionIcon']}
                />
            )}
            {children}
        </CoreButtonView>
    );
};
